import React, { useEffect, useState, useCallback } from 'react';
import { CircularProgress, FormControl, InputLabel, Select, MenuItem, Button } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { Card } from 'react-bootstrap';
import request from 'tools/request';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Toast } from 'components/shared/Toaster';
import { getReminderConfig, createReminderConfig, updateReminderConfig, deleteReminderConfig } from 'actions/client';
import ClientPortalDrawer from './ClientPortalDrawer';
import ClientPortalConfig from './ClientPortalConfig';
import CreatePortalPrompt from './CreatePortalPrompt';
import DynamicConfigDisplay from './DynamicConfigDisplay';
import ReminderConfig from './ReminderConfig';

const ClientPortalTab = ({ clientUuid, portalFlag }) => {
    const { clientId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const [portalConfigList, setPortalConfigList] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [clientPortalConfig, setClientPortalConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [resolvedClientUuid, setResolvedClientUuid] = useState(null);
    const [fetchingClient, setFetchingClient] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState('create');
    const [submitting, setSubmitting] = useState(false);
    const [resolvedPortalFlag, setResolvedPortalFlag] = useState(null);
    const [configFetched, setConfigFetched] = useState(false);

    const [dynamicConfigSubmitting, setDynamicConfigSubmitting] = useState(false);

    const [reminderConfig, setReminderConfig] = useState(undefined);
    const [reminderConfigSubmitting, setReminderConfigSubmitting] = useState(false);

    useEffect(() => {
        if (clientUuid) {
            setResolvedClientUuid(clientUuid);
            setResolvedPortalFlag(portalFlag);
        } else if (clientId && !fetchingClient && !resolvedClientUuid) {
            setFetchingClient(true);
            request(`clients/${clientId}`, 'GET', null, false, {}, null, false)
                .then((response) => {
                    setResolvedClientUuid(response?.uuid);
                    setResolvedPortalFlag(response?.externalApplicationCreationEnabled);
                })
                .catch(() => {
                    setLoading(false);
                })
                .finally(() => {
                    setFetchingClient(false);
                });
        }
    }, [clientUuid, clientId, portalFlag, fetchingClient, resolvedClientUuid]);

    const fetchReminderConfig = useCallback(() => {
        if (!resolvedClientUuid) return;
        getReminderConfig(resolvedClientUuid)
            .then((response) => {
                setReminderConfig(response || null);
            })
            .catch(() => {
                setReminderConfig(null);
            });
    }, [resolvedClientUuid]);

    const updateUrlConfig = useCallback((configId) => {
        const params = new URLSearchParams(location.search);
        if (configId) {
            params.set('config', configId);
        } else {
            params.delete('config');
        }
        history.replace({ search: params.toString() });
    }, [history, location.search]);

    const fetchClientPortalConfig = useCallback((selectLastAfterFetch = false) => {
        if (!resolvedClientUuid) return;

        setLoading(true);

        const configIdFromUrl = new URLSearchParams(location.search).get('config');

        request(`clients/${resolvedClientUuid}/portal`, 'GET', null, false, {}, null, false)
            .then((response) => {
                const list = Array.isArray(response) ? response : response ? [response] : [];
                if (list.length > 0) {
                    setPortalConfigList(list);
                    let index = selectLastAfterFetch ? list.length - 1 : 0;
                    if (!selectLastAfterFetch && configIdFromUrl) {
                        const found = list.findIndex((c) => String(c.clientFormConfigId) === configIdFromUrl);
                        if (found !== -1) index = found;
                    }
                    setSelectedIndex(index);
                    setClientPortalConfig(list[index]);
                    updateUrlConfig(list[index]?.clientFormConfigId);
                } else {
                    setPortalConfigList([]);
                    setClientPortalConfig(null);
                }
            })
            .catch(() => {
                setPortalConfigList([]);
                setClientPortalConfig(null);
            })
            .finally(() => {
                setLoading(false);
                setConfigFetched(true);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedClientUuid, updateUrlConfig]);

    useEffect(() => {
        if (resolvedClientUuid && !configFetched) {
            fetchClientPortalConfig();
            fetchReminderConfig();
        }
    }, [resolvedClientUuid, configFetched, fetchClientPortalConfig, fetchReminderConfig]);

    const handleSelectConfig = (event) => {
        const index = event.target.value;
        setSelectedIndex(index);
        setClientPortalConfig(portalConfigList[index]);
        updateUrlConfig(portalConfigList[index]?.clientFormConfigId);
    };

    const handleCreatePortal = () => {
        setDrawerMode('create');
        setDrawerOpen(true);
    };

    const handleEditPortal = () => {
        setDrawerMode('edit');
        setDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    const handleDrawerSubmit = (formData) => {
        setSubmitting(true);

        const multipartData = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
            // productName may be '' on purpose: that clears it (see ClientPortalDrawer)
            if (value !== undefined && value !== null && (value !== '' || key === 'productName')) {
                multipartData.append(key, value);
            }
        });

        const isEdit = drawerMode === 'edit';
        const clientFormConfigId = clientPortalConfig?.clientFormConfigId;
        const endpoint = isEdit && clientFormConfigId
            ? `clients/${resolvedClientUuid}/client-portal-setup/${clientFormConfigId}`
            : `clients/${resolvedClientUuid}/client-portal-setup`;
        const method = isEdit ? 'PATCH' : 'POST';

        request(endpoint, method, multipartData, false, { 'Content-Type': 'multipart/form-data' })
            .then(() => {
                Toast({
                    type: 'success',
                    message: isEdit
                        ? 'Client portal updated successfully'
                        : 'Client portal created successfully'
                });
                setDrawerOpen(false);
                fetchClientPortalConfig(!isEdit);
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const handleDynamicConfigSubmit = (configData, onSuccess) => {
        setDynamicConfigSubmitting(true);

        const clientFormConfigId = clientPortalConfig?.clientFormConfigId;
        const dynamicConfigEndpoint = clientFormConfigId
            ? `clients/${resolvedClientUuid}/dynamicConfig/${clientFormConfigId}`
            : `clients/${resolvedClientUuid}/dynamicConfig`;

        request(dynamicConfigEndpoint, 'PUT', configData)
            .then(() => {
                Toast({ type: 'success', message: 'Dynamic configuration saved successfully' });
                if (onSuccess) onSuccess();
                fetchClientPortalConfig();
            })
            .finally(() => {
                setDynamicConfigSubmitting(false);
            });
    };

    const handleReminderConfigSubmit = (data, onSuccess) => {
        setReminderConfigSubmitting(true);
        const action = reminderConfig
            ? updateReminderConfig(resolvedClientUuid, data)
            : createReminderConfig(resolvedClientUuid, data);
        action
            .then(() => {
                Toast({
                    type: 'success',
                    message: reminderConfig
                        ? 'Reminder configuration updated successfully'
                        : 'Reminder configuration created successfully'
                });
                if (onSuccess) onSuccess();
                fetchReminderConfig();
            })
            .finally(() => {
                setReminderConfigSubmitting(false);
            });
    };

    const handleReminderConfigDelete = () => {
        deleteReminderConfig(resolvedClientUuid)
            .then(() => {
                Toast({ type: 'success', message: 'Reminder configuration deleted successfully' });
                setReminderConfig(null);
            });
    };

    if (loading || fetchingClient) {
        return (
            <Card className='p-4 cardHeight'>
                <div className="text-center p-4">
                    <CircularProgress />
                </div>
            </Card>
        );
    }

    if (!resolvedClientUuid) {
        return (
            <Card className='p-4 cardHeight'>
                <div className="text-center p-4">
                    <p>Unable to load client information.</p>
                </div>
            </Card>
        );
    }

    if (portalConfigList.length === 0) {
        return (
            <>
                <CreatePortalPrompt
                    portalFlag={resolvedPortalFlag}
                    onCreatePortal={handleCreatePortal}
                />
                <ClientPortalDrawer
                    open={drawerOpen}
                    onClose={handleDrawerClose}
                    onSubmit={handleDrawerSubmit}
                    loading={submitting}
                    mode="create"
                />
            </>
        );
    }

    return (
        <>
            <Card className='p-4 mb-3'>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <FormControl variant="outlined" style={{ flex: 1 }}>
                        <InputLabel>Portal configuration</InputLabel>
                        <Select
                            value={selectedIndex}
                            onChange={handleSelectConfig}
                            label="Portal configuration"
                        >
                            {portalConfigList.map((cfg, index) => (
                                <MenuItem key={cfg.clientFormConfigId ?? cfg.clientUuid ?? index} value={index}>
                                    {cfg.pageName || cfg.subdomain || `Portal ${index + 1}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleCreatePortal}
                    >
                        New configuration
                    </Button>
                </div>
            </Card>

            <ClientPortalConfig
                config={clientPortalConfig}
                onEdit={handleEditPortal}
            />

            <DynamicConfigDisplay
                dynamicConfig={clientPortalConfig.dynamicConfig}
                onSubmit={handleDynamicConfigSubmit}
                loading={dynamicConfigSubmitting}
            />

            <ReminderConfig
                config={reminderConfig}
                onSubmit={handleReminderConfigSubmit}
                onDelete={handleReminderConfigDelete}
                loading={reminderConfigSubmitting}
            />

            <ClientPortalDrawer
                open={drawerOpen}
                onClose={handleDrawerClose}
                onSubmit={handleDrawerSubmit}
                loading={submitting}
                mode={drawerMode}
                initialData={drawerMode === 'edit' ? clientPortalConfig : null}
            />

        </>
    );
};

export default ClientPortalTab;
